import {
  WSActions,
  WSElement,
  WSFlexBox,
  WSText
} from "@wingspanhq/fe-component-library";
import { IWorkItem, WSDataTypes } from "@wingspanhq/payments/dist/interfaces";
import { IWorkDefinitionResponse } from "@wingspanhq/payments/dist/interfaces/api/workDefinition";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useMemo } from "react";
import { formatter } from "../../../../shared/utils/formatter";
import { AcceptedValuesResources } from "../FormPartialAttributes/helpers";
import { useModalEditItem } from "./ModalEditItem";
import { useModalRemoveItem } from "./ModalRemoveItem";

type Props = {
  noEdit?: boolean;
  workItem: IWorkItem;
  workDefinitions: IWorkDefinitionResponse[];
  resources: AcceptedValuesResources;
};

const getFormulaString = (
  workItem: IWorkItem,
  workDefinition: IWorkDefinitionResponse
) => {
  const calculation = workItem.calculations?.items.find(
    calc => calc.result === workItem.calculations?.result
  );

  if (!calculation) {
    return "";
  }

  const definitions = workDefinition.attributeDefinitions;

  const getInterpolatedFormula = () => {
    // convert format: "${workItem.hours} * 100" to "10 hours * 100" by definitions;
    return calculation.formula.replace(/\${(.*?)\}/g, (match, key) => {
      const field = key.split(".")[1];

      const definition = definitions.find(
        definition => definition.key === field
      );

      if (!definition) {
        return match;
      }

      if (!workItem.attributes[field]) {
        return "";
      }

      const definitionName = definition.name.toLowerCase();
      const value = workItem.attributes[field];

      switch (definition.type) {
        case WSDataTypes.Boolean:
          return `${value ? "Yes" : "No"} ${definitionName}`;
        case WSDataTypes.Datetime:
          return `${formatter.format(value, {
            dateType: "monthDayYear"
          })} ${definitionName}`;
        case WSDataTypes.Number:
        case WSDataTypes.String:
        default:
          return `${value} ${definitionName}`;
      }
    });
  };

  /*
  try {
    const interpolatedFormula = getInterpolatedFormula();
    if (interpolatedFormula) {
      return interpolatedFormula;
    }
  } catch (error) {
    console.error("Error interpolating formula:", error);
  }
  */

  return calculation.formula;
};

export const Item: React.FC<Props> = props => {
  const workDefinition = props.workDefinitions.find(
    workDefinition =>
      workDefinition.workDefinitionId === props.workItem.workDefinitionId
  );

  const attributeDescriptions = useMemo(() => {
    if (!workDefinition) {
      return [];
    }

    return Object.entries(props.workItem.attributes).map(([key, value]) => {
      const attribute = workDefinition.attributeDefinitions.find(
        attribute => attribute.key === key
      );

      if (!attribute) {
        return "";
      }

      return `${attribute.name}: ${formatter.format(value, {
        dateType: "monthDayYear"
      })}`;
    });
  }, [props.workItem, workDefinition]);

  const modalEditItem = useModalEditItem();
  const modalRemoveItem = useModalRemoveItem();

  if (!workDefinition) {
    return null;
  }

  const formulaString = getFormulaString(props.workItem, workDefinition);

  return (
    <WSElement>
      <WSFlexBox justify="space-between" wrap="nowrap" alignItems="center">
        <WSElement>
          <WSText.ParagraphSm color="gray500">
            {workDefinition?.name}
          </WSText.ParagraphSm>
          {formulaString && (
            <WSText.ParagraphXs color="gray400" mb="XS">
              {formulaString}
            </WSText.ParagraphXs>
          )}
          {attributeDescriptions.map(description => (
            <WSText.ParagraphXs key={description} color="gray400">
              {description}
            </WSText.ParagraphXs>
          ))}
        </WSElement>

        <WSElement>
          <WSText.ParagraphSm mb="L" align="right">
            {formatMoney(props.workItem.calculations?.result || 0, "USD")}
          </WSText.ParagraphSm>

          {!props.noEdit && (
            <WSActions
              buttons={[
                {
                  kind: "Link",
                  label: "Edit",
                  onClick: () => {
                    modalEditItem.open({
                      workItem: props.workItem,
                      workDefinition: workDefinition!,
                      resources: props.resources
                    });
                  }
                }
              ]}
              menuItems={[
                {
                  icon: "trash",
                  iconProps: {
                    color: "red400"
                  },
                  label: "Remove item",
                  onClick: () => {
                    modalRemoveItem.open({
                      workItemId: props.workItem.workItemId,
                      name: workDefinition?.name || ""
                    });
                  }
                }
              ]}
            />
          )}
        </WSElement>
      </WSFlexBox>
    </WSElement>
  );
};
